import { useMemo, useState } from "react";
import PageShell from "@/components/feature/PageShell";
import CategoryPage from "@/components/feature/CategoryPage";
import FilterGroup from "@/components/base/FilterGroup";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import WorkCard from "@/components/feature/WorkCard";
import { useSeo } from "@/hooks/useSeo";
import { worksList, workRegions, workTypes, workYears } from "@/mocks/works";

const PAGE_SIZE = 6;

const sortOptions = [
  { label: "收藏最多", value: "favorite" },
  { label: "最近更新", value: "updated" },
  { label: "年份最新", value: "year" },
];

export default function WorksPage() {
  useSeo(
    "age动漫 - 作品分类展示",
    "age动漫作品分类展示页，按TV动画、剧场版、OVA类型与地区、年份筛选热门动漫作品，查看作品简介、角色阵容与观众收藏数据。",
    "age动漫作品,动漫作品库,TV动画,剧场版,OVA动画推荐",
  );

  const [type, setType] = useState("全部");
  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [sort, setSort] = useState("favorite");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const list = worksList.filter(
      (item) =>
        (type === "全部" || item.type === type) &&
        (region === "全部" || item.region === region) &&
        (year === "全部" || String(item.year) === year),
    );
    return [...list].sort((a, b) => {
      if (sort === "updated") return b.updatedAt.localeCompare(a.updatedAt);
      if (sort === "year") return b.year - a.year;
      return b.favoriteCount - a.favoriteCount;
    });
  }, [type, region, year, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const current = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const reset = () => {
    setType("全部");
    setRegion("全部");
    setYear("全部");
    setSort("favorite");
    setPage(1);
  };

  return (
    <PageShell>
      <CategoryPage
        breadcrumb={[{ label: "首页", to: "/" }, { label: "作品" }]}
        kicker="WORKS"
        title="作品分类展示"
        description="这里收录了编辑部持续关注的动画作品。可以按类型、地区与年份组合筛选，也可以按收藏人数或更新时间排序，快速找到符合口味的下一部。"
        meta={
          <div className="flex items-center gap-4 text-xs text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-film-line text-primary-500"></i>
              收录 {worksList.length} 部
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-heart-3-line text-primary-500"></i>
              口碑排序
            </span>
          </div>
        }
        filterContent={
          <>
            <FilterGroup
              title="作品类型"
              options={workTypes.map((item) => ({ label: item, value: item }))}
              value={type}
              onChange={(value) => {
                setType(value);
                setPage(1);
              }}
            />
            <FilterGroup
              title="制作地区"
              options={workRegions.map((item) => ({ label: item, value: item }))}
              value={region}
              onChange={(value) => {
                setRegion(value);
                setPage(1);
              }}
              grid
            />
            <FilterGroup
              title="上映年份"
              options={workYears.map((item) => ({ label: item === "全部" ? "全部年份" : `${item} 年`, value: item }))}
              value={year}
              onChange={(value) => {
                setYear(value);
                setPage(1);
              }}
              grid
            />
            <FilterGroup
              title="排序方式"
              options={sortOptions}
              value={sort}
              onChange={(value) => {
                setSort(value);
                setPage(1);
              }}
            />
          </>
        }
        count={filtered.length}
        onReset={reset}
      >
        {current.length === 0 ? (
          <EmptyState
            title="暂无符合条件的作品"
            description="换一个类型或年份试试，也可以重置筛选条件查看全部作品。"
            icon="ri-film-line"
            action={
              <button
                type="button"
                onClick={reset}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i>
                重置筛选
              </button>
            }
          />
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4">
              {current.map((item) => (
                <WorkCard key={item.id} item={item} />
              ))}
            </div>
            <div className="mt-8">
              <Pagination
                currentPage={safePage}
                totalPages={totalPages}
                onChange={(next) => {
                  setPage(next);
                  window.scrollTo({ top: 240, behavior: "smooth" });
                }}
              />
            </div>
          </>
        )}
      </CategoryPage>
    </PageShell>
  );
}